import { Granularity, AlignMode, SeriesPoint } from '../types';

export function normalizeDate(d: string | Date): Date {
  return typeof d === 'string' ? new Date(d) : d;
}

export function aggregate(
  points: SeriesPoint[],
  granularity: Granularity,
  strategy: 'sum' | 'latest' = 'sum'
): SeriesPoint[] {
  if (granularity === 'daily') return points;

  const buckets = new Map<
    string,
    {
      points: { date: Date; value: number; currency?: string; originalDate?: string | Date; dayOffset?: number }[];
    }
  >();
  
  for (const p of points) {
    const date = normalizeDate(p.date);
    let key: string;
    if (granularity === 'weekly') {
      // Start of ISO week (Monday)
      const d = new Date(date);
      const day = d.getDay(); // 0 (Sun) - 6 (Sat)
      const diffToMonday = (day + 6) % 7; // Sun->6, Mon->0, ...
      d.setDate(d.getDate() - diffToMonday);
      d.setHours(0, 0, 0, 0);
      key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    } else {
      // First of month
      const d = new Date(date.getFullYear(), date.getMonth(), 1);
      key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
    }
    const bucket = buckets.get(key);
    const entry = {
      date,
      value: p.value || 0,
      currency: p.currency,
      originalDate: p.originalDate,
      dayOffset: p.dayOffset
    };
    if (!bucket) {
      buckets.set(key, { points: [entry] });
    } else {
      bucket.points.push(entry);
    }
  }

  return Array.from(buckets.entries())
    .sort((a, b) => new Date(a[0]).getTime() - new Date(b[0]).getTime())
    .map(([key, bucket]) => {
      const sortedPoints = bucket.points.sort((a, b) => a.date.getTime() - b.date.getTime());
      const lastPoint = sortedPoints[sortedPoints.length - 1];
      if (strategy === 'latest') {
        return {
          date: key,
          value: lastPoint.value,
          currency: lastPoint.currency,
          originalDate: lastPoint.originalDate ?? lastPoint.date,
          dayOffset: lastPoint.dayOffset
        };
      }

      const total = sortedPoints.reduce((sum, point) => sum + (point.value || 0), 0);
      const currency = sortedPoints.find((point) => point.currency)?.currency;
      return {
        date: key,
        value: total,
        currency,
        originalDate: lastPoint.originalDate ?? lastPoint.date,
        dayOffset: lastPoint.dayOffset
      };
    });
}

export function align(points: SeriesPoint[], mode: AlignMode): { dateObj: Date; index?: number; point: SeriesPoint }[] {
  if (mode === 'absolute') {
    return points
      .map((p) => ({ dateObj: normalizeDate(p.date), point: p }))
      .sort((a, b) => a.dateObj.getTime() - b.dateObj.getTime());
  }
  // relativeStart: index-based from series start
  const sorted = points
    .map((p) => ({ dateObj: normalizeDate(p.date), point: p }))
    .sort((a, b) => a.dateObj.getTime() - b.dateObj.getTime());
  return sorted.map((entry, idx) => ({ ...entry, index: idx }));
}

export function countDays(points: SeriesPoint[]): number {
  if (!points.length) return 0;
  const first = normalizeDate(points[0].date).getTime();
  const last = normalizeDate(points[points.length - 1].date).getTime();
  return Math.max(1, Math.round((last - first) / (1000 * 60 * 60 * 24)) + 1);
}
